import React, { useEffect, useMemo, useState } from "react";

import MapComponent from "./MapComponent";
import CardComponent from "./CardComponent";
import { Bars } from "react-loader-spinner";
import {
  BASE_URL,
  PPE_getApiSync,
  getValueFromLocalStorageFunc,
  notify,
} from "../../../services/functions";

import { ToastContainer } from "react-toastify";
import "./AnalitikAll.scss";
import PaginationComponent from "./PaginationComponent";
import storeMenu from "../../../stores";
import DashboardInfoCards from "./DashboardInfoCards";
import AllDevicesMap from "./AllDevicesMap";
import { GOOGLE_MAPS_API_KEY } from "../../../config/googleMaps";

const defaultCenter = {
  lat: -6.288233,
  lng: 106.826316,
};

interface Location {
  id: number;
  id_device: string;
  id_user: number;
  username: string;
  latitude: number;
  longitude: number;
  time: string;
  message_value: number;
  battery: number;
  timeZone?: string;
  utc: number | string;
  utc_offset?: number | string;
}

const parseAsUTC = (iso: string) => {
  const s = String(iso);
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  return new Date(s + "Z"); // treat as UTC jika string tanpa offset
};

const parseOffsetToMinutes = (raw?: number | string): number | undefined => {
  if (raw === null || raw === undefined) return undefined;

  if (typeof raw === "number") {
    const abs = Math.abs(raw);
    if (abs <= 14) return Math.round(raw * 60); // jam
    if (abs <= 840) return Math.round(raw); // menit
    if (abs <= 86400) return Math.round(raw / 60); // detik
    return undefined;
  }

  const s = String(raw).trim();

  const m1 = s.match(/^([+-])(\d{1,2}):?(\d{2})$/);
  if (m1) {
    const sign = m1[1] === "-" ? -1 : 1;
    const hh = parseInt(m1[2], 10);
    const mm = parseInt(m1[3], 10);
    return sign * (hh * 60 + mm);
  }

  const m2 = s.match(/^([+-])(\d{1,4})$/);
  if (m2) {
    const sign = m2[1] === "-" ? -1 : 1;
    const body = m2[2];
    if (body.length <= 2) {
      const hh = parseInt(body, 10);
      return sign * (hh * 60);
    } else if (body.length === 3 || body.length === 4) {
      const hh = parseInt(body.slice(0, body.length - 2), 10);
      const mm = parseInt(body.slice(-2), 10);
      return sign * (hh * 60 + mm);
    }
  }

  const n = Number(s);
  if (!Number.isNaN(n)) {
    if (Math.abs(n) <= 14) return Math.round(n * 60); // jam desimal
    if (Math.abs(n) <= 840) return Math.round(n); // menit
  }
  return undefined;
};

const formatUtcLabel = (raw?: number | string): string => {
  const m = parseOffsetToMinutes(raw) ?? 0;
  const sign = m >= 0 ? "+" : "-";
  const abs = Math.abs(m);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
};

const computeTimePlusUtc = (timeIso: string, utcRaw?: number | string) => {
  const baseUtc = parseAsUTC(timeIso);
  const offMin = parseOffsetToMinutes(utcRaw) ?? 0;
  const shifted = new Date(baseUtc.getTime() + offMin * 60_000);

  return {
    labelID: shifted.toLocaleString("id-ID", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "UTC",
    }),
    labelEN: shifted.toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "medium",
      timeZone: "UTC",
    }),
    utcLabel: formatUtcLabel(utcRaw),
  };
};

const DashboardAnalitikAll: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [showLoaderMain, setShowLoaderMain] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );
  const [loginTime, setLoginTime] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedUsername, setEditedUsername] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const getDataLastLocation = async () => {
    let response = await PPE_getApiSync(
      `${BASE_URL}/v1/location/lastlocationmember`,
      null,
      "application/json",
      "GET",
      getValueFromLocalStorageFunc("IOT_TOKEN") ?? "",
    );

    if (response?.statusCode) {
      return [];
    } else if (response?.detail) {
      notify(
        "error",
        "'API Location' " +
          (response?.detail?.[0]?.msg ?? response?.detail ?? ""),
        "TOP_CENTER",
      );
      return [];
    } else {
      return Array.isArray(response) ? response : [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoaderMain(true);
      const apiData = await getDataLastLocation();
      setLocations(apiData as Location[]);
      if (apiData && apiData.length > 0) {
        setSelectedLocation(apiData[0]);
      }
      setShowLoaderMain(false);
    };
    fetchData();
  }, []);

  const handleSaveEdit = async () => {
    const updatedLocation = {
      ...selectedLocation!,
      username: editedUsername,
    };

    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === updatedLocation.id ? updatedLocation : loc,
      ),
    );

    setSelectedLocation(updatedLocation);
    setIsEditing(false);
  };

  const getCustomIcon = (name: string) => {
    const firstLetter = name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" fill="#007bff" stroke="#ffffff" stroke-width="4"/>
        <text x="50%" y="55%" text-anchor="middle" font-family="Arial" font-size="24px" fill="white">${firstLetter}</text>
      </svg>
    `;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = locations.slice(indexOfFirstItem, indexOfLastItem);

  const nextPage = () => {
    if (currentPage < Math.ceil(locations.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const page = Number(e.target.value);
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(locations.length / itemsPerPage);

  // Fungsi untuk mendapatkan zona waktu berdasarkan koordinat
  const getTimeZone = async (latitude: number, longitude: number) => {
    try {
      const timestamp = Math.floor(Date.now() / 1000);
      const apiKey = GOOGLE_MAPS_API_KEY;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/timezone/json?location=${latitude},${longitude}&timestamp=${timestamp}&key=${apiKey}`,
      );
      const data = await response.json();
      //console.log("data zona", data);
      return data.timeZoneId || "UTC";
    } catch (error) {
      console.error("Error fetching timezone:", error);
      return "UTC";
    }
  };

  useEffect(() => {
    if (selectedLocation) {
      getTimeZone(selectedLocation.latitude, selectedLocation.longitude).then(
        (zone) => {
          setSelectedLocation((prev) => ({
            ...prev!,
            timeZone: zone,
          }));
        },
      );
      const userLoginTime = new Date().getTime().toString();
      if (userLoginTime) {
        setLoginTime(userLoginTime);
      }
    }
  }, [selectedLocation]);

  //console.log("data last update", selectedLocation);

  useEffect(() => {
    setTimeout(() => {
      // dispatch icon title
      storeMenu.dispatch({ type: "titleicon", text: "Command Center" });

      // dispatch breadcrumb path
      storeMenu.dispatch({
        type: "breadcrumbs",
        text: [
          { key: "Dashboard", value: "Dashboard" },
          { key: "Sites", value: "Command Center" },
        ],
      });
    }, 100);
  }, []);

  const devicePoints = useMemo(
    () =>
      locations.map((l) => ({
        id: l.id,
        id_device: l.id_device,
        lat: l.latitude,
        lng: l.longitude,
        username: l.username,
        time: l.time,
        battery: l.battery,
        utc: l.utc ?? l.utc_offset,
      })),
    [locations],
  );

  return (
    <>
      <div className="ppe-anly">
        {showLoaderMain ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <Bars width="80" height="80" visible color="#4fa94d" />
          </div>
        ) : (
          <div>
            <div className="row">
              <DashboardInfoCards totalPerson={locations.length} />
            </div>

            {/* Layout Google Maps & Card */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "30px",
                flexDirection: window.innerWidth < 768 ? "column" : "row",
                gap: "20px",
              }}
            >
              <MapComponent
                selectedLocation={selectedLocation}
                defaultCenter={defaultCenter}
                mapContainerStyle={{
                  width: window.innerWidth < 768 ? "100%" : "85%",
                  height: "500px",
                }}
                getCustomIcon={getCustomIcon}
              />

              {selectedLocation && (
                <CardComponent
                  selectedLocation={selectedLocation}
                  isEditing={isEditing}
                  editedUsername={editedUsername}
                  handleSaveEdit={handleSaveEdit}
                  setIsEditing={setIsEditing}
                  setEditedUsername={setEditedUsername}
                  loginTime={loginTime}
                />
              )}
            </div>

            {/* Tabel Data dengan Pagination */}
            <div>
              <div className="table-container">
                <table className="table table-striped table-hover table-bordered">
                  <thead className="thead-light">
                    <tr>
                      <th>No</th>
                      <th>Name</th>
                      <th>Last Update</th>
                      <th>Battery</th>
                    </tr>
                  </thead>
                  {/* <tbody className="text-center">
                      {currentItems.map((location, index) => (
                        <tr
                          key={location.id}
                          onClick={() => setSelectedLocation(location)}
                          style={{
                            cursor: "pointer",
                            background:
                              selectedLocation?.id === location.id
                                ? "#f0f0f0"
                                : "transparent",
                          }}
                        >
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>{location.username}</td>
                          <td>{new Date(location.time).toLocaleString()}</td>
                          <td>{location.message_value}%</td>
                        </tr>
                      ))}
                    </tbody> */}
                  <tbody className="text-center">
                    {currentItems.map((location, idx) => {
                      const battery = location.battery ?? 0;
                      const colorClass =
                        battery >= 70
                          ? "bg-success"
                          : battery >= 30
                            ? "bg-warning"
                            : "bg-danger";
                      const off = location.utc ?? location.utc_offset;
                      const lastUpdate =
                        off !== undefined && off !== null
                          ? computeTimePlusUtc(location.time, off)
                          : null;
                      return (
                        <tr
                          key={location.id}
                          style={{
                            cursor: "pointer",
                            background:
                              selectedLocation?.id === location.id
                                ? "#f0f0f0"
                                : "transparent",
                          }}
                          onClick={() => setSelectedLocation(location)}
                        >
                          <td>{indexOfFirstItem + idx + 1}</td>
                          <td>{location.username}</td>
                          <td>
                            {lastUpdate ? (
                              <>
                                {lastUpdate.labelID}{" "}
                                <small className="text-muted">
                                  ({lastUpdate.utcLabel})
                                </small>
                              </>
                            ) : (
                              new Date(location.time).toLocaleString("id-ID", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })
                            )}
                          </td>
                          <td>
                            <span className={`badge text-white ${colorClass}`}>
                              {battery} %
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>

            <div className="map-section card shadow-sm border-0 mt-4 overflow-hidden">
              <div className="d-flex align-items-center justify-content-between p-3 map-section__header bg-white">
                <div className="d-flex align-items-center gap-3">
                  <span className="map-section__icon d-inline-flex align-items-center justify-content-center">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 22s7-7.15 7-12a7 7 0 10-14 0c0 4.85 7 12 7 12z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="12"
                        cy="10"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </span>
                  <div>
                    <h5 className="mb-0 fw-semibold map-section__title">
                      All Devices Map
                    </h5>
                    <small className="text-muted">
                      Live last location overview
                    </small>
                  </div>
                </div>

                <span className="badge badge-soft-primary px-3 py-2">
                  {devicePoints.length} devices
                </span>
              </div>

              <div className="map-section__body p-0">
                <div className="map-shell">
                  <AllDevicesMap
                    devices={devicePoints}
                    defaultCenter={defaultCenter}
                    mapContainerStyle={{ width: "100%", height: "500px" }}
                    getCustomIcon={getCustomIcon}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer draggable pauseOnHover />
    </>
  );
};

export default DashboardAnalitikAll;
